/**
 * LoudnessStage — the loudness step of an audio chain, decided ONCE per
 * (file, chain before it) and given identically to the preview and the export.
 *
 * Why it exists (/audio-enhancer, support chat 26.09.2026: «активирую ползунок
 * „Выравнивание громкости“, а в окончательном результате он как бы сбрасывается
 * и ничего не улучшается»). Measured on real songs through the real page:
 *
 *   1. The preview ran one-pass loudnorm over its first 15 s. One-pass loudnorm
 *      is always DYNAMIC and compares every 3-s window with the loudness heard
 *      SO FAR, so it pulls whatever the file starts with up to the target. The
 *      export measured the whole file and applied one gain. Preview vs the first
 *      15 s of the file: +0.6…+7.1 LU louder over 15 s, up to 24.8 LU in single
 *      seconds, on 9 of 10 tracks — the preview promised what the file did not do.
 *   2. Whether the FILE was levelled was decided by its true peak, not by the
 *      person: after the chain's EQ most masters peak above -1 dBTP, loudnorm
 *      then refuses linear=true and falls back to that same dynamic mode (6 of
 *      9 tracks) — intros lifted with the switch OFF.
 *   3. The switch itself only changed loudnorm's LRA target (14 -> 7). In linear
 *      mode LRA is ignored; in dynamic mode it widens or narrows a ±LRA/2 window
 *      the material rarely leaves. Measured: identical loudness with it on and
 *      off; on its own it raised pauses and fade tails by +5 dB.
 *   4. alimiter's `level` (auto-level) is ON by default in FFmpeg: `limit=0.95`
 *      re-amplified everything by 1/0.95 and put peaks back at 0 dBFS — +0.4 dBTP
 *      in the file against the promised -1.
 *
 * What it does instead, all measured:
 *   - ONE pass over the whole file (FFmpegProcessor.analyzeLoudnessSeries:
 *     ebur128 behind the same chain) returns momentary and short-term loudness
 *     every 100 ms. 2.9 s on a 200-s song in the browser build, against 45 s for
 *     the loudnorm measure pass it replaces.
 *   - Levelling («rider», only when asked for): sections quieter than the
 *     programme by more than `dead` LU are raised by k·(gap−dead), louder ones
 *     lowered, never pauses, silence, fades or builds (a ramp that runs into
 *     silence). The whole file is known, so the gain curve is smoothed with no
 *     lag. Body untouched (gain std ≤ 0.1 dB), micro-dynamics untouched (crest
 *     factor ±0.02 dB), fades ≤ 2 dB.
 *   - Normalisation: ONE static gain to the target, computed from the same
 *     series (BS.1770 gating over the 400-ms blocks, with the rider applied) —
 *     predicted I equals the measured file within 0.1 LU, so no second pass.
 *   - Peaks: alimiter at 4× oversampling with auto-level OFF — the only variant
 *     that held -1 dBTP (sample-rate alimiter: +1.1 dBTP inter-sample).
 *
 * The preview renders the SAME filters over its slice (the curve is absolute in
 * time, the gain is a number): preview == file, by construction.
 *
 * Speech-like presets came over the same evening. Their loudnorm tail (−16 LUFS,
 * LRA 8/11) had the same two faces, and on 15 real speech recordings the preview
 * came out +3.4…+6.9 LU louder than the file where the file starts unlike its
 * body (old radio, a crowd before a speech, a quiet-start interview; 13–18 LU
 * in single seconds of a podcast's music intro). Here they get the static gain,
 * a −1.5 dBTP ceiling and the `range` rider holding their LRA; and their export
 * halves: the loudnorm measure pass was a 192-kHz dynamic loudnorm over the
 * whole chain (5-min speech in the browser build: 62.7 s export before, 21.0 s
 * once the preview has measured, 27.7 s without a preview).
 */
(function (root) {
    'use strict';

    // ── Presets of the rider. Numbers are the measured choice, not taste:
    //    music — corpus of songs, 2026-09-27 (tests/scratch/aelev);
    //    speech — interview with speakers 15.7 LU apart;
    //    range — a speech-like preset's loudness-range target (opts.lra): nothing
    //    within ±lra/2 of the programme, anything beyond it back to that edge,
    //    pauses and fades kept out. It is what loudnorm's LRA target means, but
    //    decided from the whole file — loudnorm decides it causally, pulling the
    //    first seconds to the target and learning the rest on the way. Rendered
    //    on the six widest-range of 15 real speech recordings × 3 presets: the
    //    loudness range of the old loudnorm files within 0.38 LU on average
    //    (static gain alone: 1.52 — an interview 17.3 LU wide instead of 14.1),
    //    I within 0.3 LU, pauses at most 2.5 dB louder
    //    (tests/scratch/aelev/speech-bench-scratch.mjs, PREV_DIR mode).
    //    whisper — whisper and quiet speech between phrases of a normal voice
    //    (support chat 705, 2026-10-22): the envelope is MOMENTARY (`src: 'M'`, a
    //    whispered phrase is shorter than the 3-s window), and a pause is not
    //    «20 LU under the programme» — after the noise is taken out whisper lives
    //    exactly there, 20–27 LU down — but this recording's own floor: the 10th
    //    percentile of momentary loudness + 6 LU (`floorP`, `floorMargin`). On four
    //    whisper dialogues (tests/asr-bench/whisper-dialog) the quiet whisper in a
    //    noisy room went 0.18–0.34 → 0.36–0.66 ESTOI, beating a fast dynaudnorm on
    //    every one; normal speech 0.953 → 0.951. `maxGain`: phone recordings of a
    //    whisper sit at −65 LUFS, and 30 dB left them at −35.
    var RIDER = {
        music:  { kUp: 0.8, kDn: 0.5, dead: 1.0, upMax: 10, dnMax: 6, quietFloor: 20, taper: 6, absFloor: -55, sigma: 0.6, slope: 0.3, slopeWin: 3 },
        speech: { kUp: 0.9, kDn: 0.6, dead: 1.0, upMax: 15, dnMax: 8, quietFloor: 20, taper: 6, absFloor: -60, sigma: 0.5, slope: 0.3, slopeWin: 3 },
        range:  { kUp: 1, kDn: 1, dead: 5.5, upMax: 10, dnMax: 12, quietFloor: 20, taper: 6, absFloor: -60, sigma: 1.0, slope: 0.3, slopeWin: 3 },
        whisper: { src: 'M', kUp: 1, kDn: 0.6, dead: 1.0, upMax: 24, dnMax: 8, quietFloor: 40, taper: 6, absFloor: -60,
                   floorP: 10, floorMargin: 6, sigma: 0.15, slope: 99, slopeWin: 0.3, maxGain: 45 }
    };

    function riderParams(name, opts) {
        var p = RIDER[name];
        if (!p || name !== 'range') return p || null;
        var lra = opts && opts.lra > 0 ? opts.lra : 2 * p.dead;
        var q = {}, k;
        for (k in p) q[k] = p[k];
        q.dead = lra / 2;
        return q;
    }

    var ABS_GATE = -70;          // BS.1770 absolute gate, LUFS
    var MAX_GAIN_DB = 30;        // sanity: never more than this in one static gain
    var MAX_POINTS = 240;        // breakpoints of the rider curve in the expression

    // ── BS.1770 integrated loudness from momentary blocks (400 ms, 100 ms hop =
    //    exactly the gating blocks of the standard). `add` (optional) is a gain
    //    in dB per block, applied before gating.
    function gatedIntegrated(M, add) {
        var n = M.length, i, sum = 0, cnt = 0;
        var v = new Float64Array(n);
        for (i = 0; i < n; i++) v[i] = M[i] + (add ? add[i] : 0);
        for (i = 0; i < n; i++) if (v[i] > ABS_GATE) { sum += Math.pow(10, v[i] / 10); cnt++; }
        if (!cnt) return -Infinity;
        var rel = 10 * Math.log10(sum / cnt) - 10;
        sum = 0; cnt = 0;
        for (i = 0; i < n; i++) if (v[i] > ABS_GATE && v[i] > rel) { sum += Math.pow(10, v[i] / 10); cnt++; }
        return cnt ? 10 * Math.log10(sum / cnt) : -Infinity;
    }

    // Linear fit of the last/first `fit` points — extends a curve past its end
    // keeping its slope, so a fade stays a fade instead of a flat line.
    function extend(x, nLeft, nRight, fit) {
        fit = Math.max(2, Math.min(fit || 20, x.length));
        var out = new Float64Array(nLeft + x.length + nRight), i;
        function slopeOf(a, b) {
            var n = b - a, sx = 0, sy = 0, sxy = 0, sxx = 0;
            for (var k = 0; k < n; k++) { var y = x[a + k]; sx += k; sy += y; sxy += k * y; sxx += k * k; }
            var d = n * sxx - sx * sx;
            return d ? (n * sxy - sx * sy) / d : 0;
        }
        var sL = nLeft ? slopeOf(0, fit) : 0, sR = nRight ? slopeOf(x.length - fit, x.length) : 0;
        for (i = 0; i < nLeft; i++) out[i] = x[0] - sL * (nLeft - i);
        for (i = 0; i < x.length; i++) out[nLeft + i] = x[i];
        for (i = 0; i < nRight; i++) out[nLeft + x.length + i] = x[x.length - 1] + sR * (i + 1);
        return out;
    }

    // Short-term loudness printed at t describes [t-3, t]; L[i] = the value that
    // describes the moment of frame i (lag 1.5 s), extrapolated past the end.
    function centred(S, dt, lagS) {
        var sh = Math.round(lagS / dt), n = S.length, x = new Float64Array(n), i;
        var cl = new Float64Array(n);
        for (i = 0; i < n; i++) cl[i] = Math.max(S[i], ABS_GATE);
        var e = extend(cl, 0, sh, 20);
        for (i = 0; i < n; i++) x[i] = e[i + sh];
        return x;
    }

    function movingAverage(x, k) {
        var n = x.length, out = new Float64Array(n), i, h = Math.floor(k / 2);
        var e = extend(x, k, k, 20), acc = 0;
        for (i = 0; i < k; i++) acc += e[k - h + i];
        for (i = 0; i < n; i++) {
            out[i] = acc / k;
            acc += e[k - h + i + k] - e[k - h + i];
        }
        return out;
    }

    // Frames that belong to a ramp touching silence (fade-in out of the floor,
    // fade-out into it, a build from nothing) — and the floor itself.
    function fadeMask(Ls, floorLevel, slope, s0, dt) {
        var n = Ls.length, m = new Uint8Array(n), i, j, q, k = Math.round(2 / dt);
        var sgn = new Int8Array(n);
        for (i = 0; i < n; i++) sgn[i] = slope[i] > s0 ? 1 : (slope[i] < -s0 ? -1 : 0);
        for (i = 0; i < n; i++) if (Ls[i] < floorLevel) m[i] = 1;
        i = 0;
        while (i < n) {
            if (!sgn[i]) { i++; continue; }
            j = i;
            while (j + 1 < n && sgn[j + 1] === sgn[i]) j++;
            var touches = false;
            if (sgn[i] < 0) {
                if (j === n - 1) touches = true;
                for (q = j; q <= Math.min(n - 1, j + k) && !touches; q++) if (Ls[q] < floorLevel) touches = true;
            } else {
                if (i === 0) touches = true;
                for (q = Math.max(0, i - k); q <= i && !touches; q++) if (Ls[q] < floorLevel) touches = true;
            }
            if (touches) for (q = i; q <= j; q++) m[q] = 1;
            i = j + 1;
        }
        return m;
    }

    function gaussianSmooth(c, dt, sigma) {
        var r = Math.round(3 * sigma / dt), n = c.length, i, j;
        if (r < 1) return c;
        var ker = new Float64Array(2 * r + 1), s = 0;
        for (j = -r; j <= r; j++) { ker[j + r] = Math.exp(-0.5 * Math.pow(j * dt / sigma, 2)); s += ker[j + r]; }
        for (j = 0; j < ker.length; j++) ker[j] /= s;
        var out = new Float64Array(n);
        for (i = 0; i < n; i++) {
            var acc = 0;
            for (j = -r; j <= r; j++) {
                var k = i + j; if (k < 0) k = 0; else if (k >= n) k = n - 1;
                acc += ker[j + r] * c[k];
            }
            out[i] = acc;
        }
        return out;
    }

    /**
     * The rider's gain curve (dB per 100-ms frame) for a measured series.
     * @param {{dt:number,S:ArrayLike<number>}} series
     * @param {number} ref  programme loudness (integrated) the rider levels towards
     * @param {object} p    RIDER.music / RIDER.speech
     * @param {number} [lift] the static gain the plan puts after the rider (dB): the
     *   curve is decided at the level the LISTENER hears. Its absolute levels — the
     *   −70 LUFS clamp, the pause floor `absFloor` — mean «silence» and «a pause» in
     *   what is played, not in the raw file: a recording made 40 dB too quiet has
     *   its quiet speech near −74 LUFS, and in the raw domain every quiet word read
     *   as a pause (support chat 705, whisper in a phone recording at −58 dBFS: boost
     *   3 dB where the same shape at a normal level gets 15, measured 2026-10-22).
     */
    function riderCurve(series, ref, p, lift) {
        lift = isFinite(lift) ? lift : 0;
        var dt = series.dt || 0.1, n = series.S.length, i;
        var src = series.S;
        if (p.src === 'M') {
            // momentary loudness, laid where centred(·, 1.5) reads the middle of its 400-ms block
            src = new Float64Array(n);
            for (i = 0; i < n; i++) src[i] = series.M[Math.min(n - 1, Math.max(0, i - 13))];
        }
        var S = src;
        if (lift) {
            S = new Float64Array(n);
            for (i = 0; i < n; i++) S[i] = src[i] > -120 ? src[i] + lift : src[i];
            ref += lift;
        }
        var absFloor = p.absFloor;
        if (p.floorP > 0) {
            // this recording's own pause level: a low percentile of momentary loudness
            var v = [];
            for (i = 0; i < series.M.length; i++) if (series.M[i] > -100) v.push(series.M[i]);
            if (v.length) {
                v.sort(function (x, y) { return x - y; });
                absFloor = v[Math.floor(v.length * p.floorP / 100)] + lift + (p.floorMargin || 0);
            }
        }
        var L = centred(S, dt, 1.5);
        var c = new Float64Array(n), w = new Float64Array(n);
        for (i = 0; i < n; i++) {
            var d = L[i] - ref;
            if (d < -p.dead) c[i] = Math.min(p.kUp * (-(d + p.dead)), p.upMax);
            else if (d > p.dead) c[i] = -Math.min(p.kDn * (d - p.dead), p.dnMax);
            // a pause is not a quiet part: fade the boost out below the floor
            var wf = Math.min(1, Math.max(0, 1 + (L[i] - (ref - p.quietFloor)) / p.taper));
            var wa = Math.min(1, Math.max(0, 1 + (L[i] - absFloor) / p.taper));
            w[i] = Math.min(wf, wa);
        }
        var k = Math.max(1, Math.round(p.slopeWin / dt));
        var Ls = movingAverage(L, k);
        var slope = new Float64Array(n);
        for (i = 0; i < n; i++) {
            var a = i > 0 ? Ls[i - 1] : Ls[i], b = i < n - 1 ? Ls[i + 1] : Ls[i];
            slope[i] = (b - a) / ((i > 0 && i < n - 1 ? 2 : 1) * dt);
        }
        var fm = fadeMask(Ls, ref - p.quietFloor, slope, p.slope, dt);
        var fadeFrames = 0;
        for (i = 0; i < n; i++) {
            if (fm[i]) { w[i] = 0; fadeFrames++; }
            if (c[i] > 0) c[i] *= w[i];
        }
        var out = gaussianSmooth(c, dt, p.sigma);
        out.fadeSeconds = fadeFrames * dt;
        return out;
    }

    // Ramer–Douglas–Peucker over the curve (index units); the tolerance grows
    // until the point count fits the expression budget.
    function simplify(c, eps, maxPts) {
        var n = c.length;
        function run(tol) {
            var keep = new Uint8Array(n); keep[0] = 1; keep[n - 1] = 1;
            var stack = [[0, n - 1]];
            while (stack.length) {
                var ab = stack.pop(), a = ab[0], b = ab[1];
                if (b <= a + 1) continue;
                var best = -1, bi = -1;
                for (var i = a + 1; i < b; i++) {
                    var y = c[a] + (c[b] - c[a]) * (i - a) / (b - a);
                    var dv = Math.abs(c[i] - y);
                    if (dv > best) { best = dv; bi = i; }
                }
                if (best > tol) { keep[bi] = 1; stack.push([a, bi]); stack.push([bi, b]); }
            }
            var idx = [];
            for (var j = 0; j < n; j++) if (keep[j]) idx.push(j);
            return idx;
        }
        var tol = eps, idx = run(tol);
        while (idx.length > maxPts) { tol *= 1.5; idx = run(tol); }
        return { idx: idx, tol: tol };
    }

    // Piecewise-linear dB curve as an FFmpeg expression of t. Terms are summed
    // in groups of 16 so the parse tree stays shallow for long files.
    function volumeExpr(xs, ys) {
        var terms = [], i;
        for (i = 0; i < xs.length - 1; i++) {
            var dy = ys[i + 1] - ys[i];
            if (Math.abs(dy) < 1e-4) continue;
            var span = Math.max(xs[i + 1] - xs[i], 1e-3);
            terms.push((dy >= 0 ? '+' : '') + dy.toFixed(3) + '*clip((t-' + xs[i].toFixed(2) + ')/' + span.toFixed(2) + ',0,1)');
        }
        var groups = [];
        for (i = 0; i < terms.length; i += 16) groups.push('(0' + terms.slice(i, i + 16).join('') + ')');
        var sum = ys.length ? ys[0].toFixed(3) : '0';
        if (groups.length) sum += '+' + groups.join('+');
        return 'pow(10,(' + sum + ')/20)';
    }

    function evalCurveAt(xs, ys, t) {
        if (!xs.length) return 0;
        if (t <= xs[0]) return ys[0];
        for (var i = 0; i < xs.length - 1; i++) {
            if (t <= xs[i + 1]) return ys[i] + (ys[i + 1] - ys[i]) * (t - xs[i]) / (xs[i + 1] - xs[i]);
        }
        return ys[ys.length - 1];
    }

    // Frame i of the ebur128 log is printed at t = (i+1)·dt; L[i] describes that
    // moment, and the momentary block printed there covers [t-0.4, t] (middle t-0.2).
    function frameTime(i, dt) { return (i + 1) * dt; }

    function curveOf(series, ref, p, lift) {
        var dt = series.dt || 0.1, n = series.S.length, i;
        var c = riderCurve(series, ref, p, lift);
        var sim = simplify(c, 0.1, MAX_POINTS);
        var xs = [], ys = [];
        for (i = 0; i < sim.idx.length; i++) {
            xs.push(+frameTime(sim.idx[i], dt).toFixed(2));
            ys.push(+c[sim.idx[i]].toFixed(3));
        }
        var up = 0, dn = 0;
        for (i = 0; i < n; i++) { if (c[i] > up) up = c[i]; if (c[i] < dn) dn = c[i]; }
        return { x: xs, y: ys, tol: sim.tol, maxBoostDb: up, maxCutDb: dn, fadeSec: c.fadeSeconds };
    }

    // Gain (dB) the simplified curve applies to each momentary block.
    function blockGains(M, dt, curve) {
        var out = new Float64Array(M.length);
        for (var i = 0; i < M.length; i++) out[i] = evalCurveAt(curve.x, curve.y, frameTime(i, dt) - 0.2);
        return out;
    }

    function basePlan(opts, I0) {
        return { target: typeof opts.target === 'number' ? opts.target : -14,
                 ceilingDbtp: typeof opts.ceilingDbtp === 'number' ? opts.ceilingDbtp : -1,
                 boostOnly: !!opts.boostOnly, rider: null,
                 measuredI: isFinite(I0) ? +I0.toFixed(2) : null, predictedI: null, gainDb: 0,
                 silent: !(isFinite(I0) && I0 > ABS_GATE), stats: {} };
    }

    function setRider(out, preset, curve) {
        out.rider = { preset: preset, x: curve.x, y: curve.y, tol: +curve.tol.toFixed(3) };
        out.stats.maxBoostDb = +curve.maxBoostDb.toFixed(2);
        out.stats.maxCutDb = +curve.maxCutDb.toFixed(2);
        out.stats.fadeSec = +(curve.fadeSec || 0).toFixed(1);
        out.stats.points = curve.x.length;
    }

    function finishPlan(out, Iafter, maxGain) {
        var g = out.target - Iafter, mg = maxGain > 0 ? maxGain : MAX_GAIN_DB;
        if (out.boostOnly && g < 0) g = 0;
        g = Math.max(-mg, Math.min(mg, g));
        out.gainDb = +g.toFixed(2);
        out.predictedI = +(Iafter + out.gainDb).toFixed(2);
        return out;
    }

    /**
     * Decide the loudness stage from a whole-file series.
     * @param series {dt, M, S}   FFmpegProcessor.analyzeLoudnessSeries
     * @param opts   {target, ceilingDbtp, boostOnly, rider: 'music'|'speech'|'range'|null,
     *                lra: the range the 'range' rider holds, LU}
     * @returns {{gainDb, rider, measuredI, predictedI, ceilingDbtp, silent, stats}}
     */
    function plan(series, opts) {
        opts = opts || {};
        var dt = series.dt || 0.1;
        var I0 = gatedIntegrated(series.M);
        var out = basePlan(opts, I0);
        if (out.silent) return out;
        var add = null;
        var p = opts.rider ? riderParams(opts.rider, opts) : null;
        var maxG = (p && p.maxGain) || MAX_GAIN_DB;
        if (p) {
            // what the static gain will add — the floor is judged where it is heard
            var lift = Math.max(-maxG, Math.min(maxG, out.target - I0));
            var curve = curveOf(series, I0, p, lift);
            setRider(out, opts.rider, curve);
            if (opts.rider === 'range') out.rider.lra = 2 * p.dead;
            add = blockGains(series.M, dt, curve);
        }
        return finishPlan(out, add ? gatedIntegrated(series.M, add) : I0, maxG);
    }

    /** The rider alone — no gain, no limiter. For a chain that must level
     *  BEFORE it runs (speech: a noise gate inside the chain would take a quiet
     *  speaker for a pause), measured on the raw input. */
    function riderFilters(pl) {
        if (!pl.rider || pl.rider.x.length < 2) return [];
        return ['asetnsamples=n=480:p=0', "volume='" + volumeExpr(pl.rider.x, pl.rider.y) + "':eval=frame"];
    }

    /** The filters of the stage, appended after the caller's chain. */
    function filters(pl, opts) {
        opts = opts || {};
        var f = riderFilters(pl);
        if (Math.abs(pl.gainDb) >= 0.01) f.push('volume=' + pl.gainDb.toFixed(2) + 'dB');
        var lim = Math.pow(10, pl.ceilingDbtp / 20);
        var rate = opts.outRate || 48000;
        // 4× oversampled so the ceiling is a TRUE-peak ceiling; auto-level OFF.
        f.push('aresample=' + (rate * 4));
        f.push('alimiter=limit=' + lim.toFixed(4) + ':attack=5:release=50:level=disabled');
        f.push('aresample=' + rate);
        return f;
    }

    // ── FFmpeg-facing part ────────────────────────────────────────────────
    var _memo = new Map();
    function fileKey(file) {
        return file ? [file.name || '', file.size || 0, file.lastModified || 0].join('|') : '';
    }

    /**
     * The series of what `preChain` produces from the WHOLE `file`, memoised per
     * (file, chain) for the page's life — the preview measures, the export
     * reuses. (Sampling windows of a long file instead was measured on a 27-min
     * mix: 19.0 s for 13 windows vs 24.1 s for all of it, 0.77 dB off — no.)
     */
    async function measure(file, preChain) {
        var P = root.FFmpegProcessor;
        if (!P || typeof P.analyzeLoudnessSeries !== 'function') throw new Error('LoudnessStage: FFmpegProcessor.analyzeLoudnessSeries missing');
        var key = fileKey(file) + '#' + (preChain || '');
        var hit = _memo.get(key);
        if (hit) return hit;
        var s = await P.analyzeLoudnessSeries(file, preChain, {});
        _memo.set(key, s);
        return s;
    }

    /** Memoised whole-file series if it exists — never measures. */
    function known(file, preChain) {
        return _memo.get(fileKey(file) + '#' + (preChain || '')) || null;
    }

    function forget() { _memo.clear(); }

    var api = {
        RIDER: RIDER,
        plan: plan, filters: filters, riderFilters: riderFilters,
        measure: measure, known: known, forget: forget,
        fileKey: fileKey,
        _math: { gatedIntegrated: gatedIntegrated, riderCurve: riderCurve, simplify: simplify,
                 volumeExpr: volumeExpr, evalCurveAt: evalCurveAt, centred: centred, fadeMask: fadeMask,
                 frameTime: frameTime }
    };
    root.LoudnessStage = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
